import { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import toast from '../../elements/Toast/toast';
import { Dispatch } from "redux"
import { useHistory, useParams } from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { IProductPageProps, IProductsParamsProps } from './index';
import "./styles.scss";

import { Card } from "@merchstores/shared/elements/Card";
import { Gallery } from "@merchstores/shared/elements/Gallery";
import { FormSelect, IFormOptionProps } from "@merchstores/shared/elements/FormSelect";
import { FormInputNumber } from "@merchstores/shared/elements/FormInputNumber";
import { CTA } from "@merchstores/shared/elements/Cta";
import { getLocalDateText } from '../DateTime';
import { Loading } from "@merchstores/frontend/components/Loading"; 
import { generateCloudinaryMockupUrl } from "@merchstores/shared/components/Cloudinary";
import { addToCart } from '../../actions/cart';
import { ICart, ICartItem, IMerchStore, IProductVariant, IStoreState, IUser, Scalar } from '../../reduxTypes';
import { useRef } from 'react';
import Scroll from 'react-scroll';

export const ProductPage = (props: IProductPageProps) => { // Define the actual props needed in the index file
    const dispatch: Dispatch<any> = useDispatch()
    const history = useHistory();
    const scroll = Scroll.animateScroll;

    const { productHandle } = useParams<IProductsParamsProps>();

    const { register, setValue, formState: { errors } } = useForm();

    const [expandArtwork, setExpandArtwork] = useState(false);

    const [selectedVariant, setSelectedVariant] = useState({ id: "", quantityAvailable: 0 });

    const [product, setProduct] = useState(props.product);
    const [description, setDescription] = useState("")

    const merchstore: IMerchStore = useSelector((state: IStoreState) => state.merchstore, shallowEqual);
    const cart: ICart = useSelector((state: IStoreState) => state.cart, shallowEqual);
    const user: IUser = useSelector((state: IStoreState) => state.user, shallowEqual);

    const [shortendedDescription, setShortenedDescription] = useState("")
    const [decorationTypes, setDecorationTypes] = useState([]);
    const [decorationLocations, setDecorationLocations] = useState([]);
    const [artworkOptions, setArworkOptions] = useState([]);
    const [logoMockup, setLogoMockup] = useState("");
    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const [selectedArtwork, setSelectedArtwork] = useState("");
    const [selectedDecorationType, setSelectedDecorationType] = useState("");
    const [selectedDecorationLocation, setSelectedDecorationLocation] = useState("");
    const [productImages, setProductImages] = useState([]);
    const [loading, setLoading] = useState(true)
    const [isSoldOut, setIsSoldOut] = useState(true)

    const productInfoRef = useRef(null);

    useEffect(() => {
        if (props.product) {
            setProduct(props.product);

            const product = props.product;

            if (product.descriptionHtml) {               
                const descriptionWithoutDetails = product.descriptionHtml.replace(/<details[^]*<\/details>/gi, "")
                // if there is a <p> tag in the descriptionHtml, remove it. If not, just use descriptionWithoutDetails
                const descriptionWithoutPTag = descriptionWithoutDetails.search('<p>') ?
                    descriptionWithoutDetails.replace(/<p[^]*<\/p>/gi, "")
                    : descriptionWithoutDetails
                setDescription(descriptionWithoutPTag)
            }
            if (product.description) {
                product.description.length > 150 ?
                    setShortenedDescription(product.description.substring(0, 150) + "...")
                    : setShortenedDescription(product.description);
            }

            if (product.tags) {
                const decorationTypes = product.tags.filter((t: any) => t.value.includes("deco_type="));
                setDecorationTypes(decorationTypes.map((d: any) => d.value.split("=")[1]));

                const decorationLocations = product.tags.filter((t: any) => t.value.includes("deco_location="));
                setDecorationLocations(decorationLocations.map((d: any) => d.value.split("=")[1]));
            }

            if (product.variants && product.variants.length > 0) {
                setSelectedVariant(product.variants[0]);
            }
            setLoading(false)
        } else {
            console.log(`No product found with handle ${productHandle}`);
            history.push("/404");
        }

    }, [props.product]);

    useEffect(() => {

        if (product.id !== "" && merchstore) {
            if (product.tags) {
                const artworkOption = product.tags.find((t: Scalar) => t.value.includes("artwork_option="));
                const artworkIndex = artworkOption ? parseInt(artworkOption.value.split("=")[1]) : 1;
                const merchstoreArtworks = merchstore.artworkOptions;
                if (merchstoreArtworks) {
                    setArworkOptions([merchstoreArtworks[artworkIndex - 1]]);
                    const logoTag = [product.tags.find((t: Scalar) => t.value.includes("LogoArea=")).value];
                    const mockupUrl = generateCloudinaryMockupUrl(850, 850, logoTag, merchstoreArtworks[artworkIndex - 1], product.images[0].src, "merchstores");
                    setLogoMockup(mockupUrl);
                    const productImageArr = product.images.map((i: any) => { return i.src });
                    productImageArr[0] = mockupUrl;
                    setProductImages(productImageArr);
                }

                const isProductFromMerchstore = product.tags.find((t: Scalar) => t.value.includes(`merchstore_code=${merchstore.storeCode}`));
                if (!isProductFromMerchstore) { // The product is from a different store, redirect to 404
                    console.log("The product is from a different store, redirect to 404");
                    history.push("/404");
                }
            }
        }
        
    }, [product, merchstore]);

    useEffect(() => {
        if(product) {
            for(const variant of product.variants) {
                if(variant.quantityAvailable > 0) {
                    setIsSoldOut(false)
                }
            }
        }
    }, [product])
    
    const expandFunction = () => {
        setExpandArtwork(true);
    }

    const closeExpandFunction = () => {
        setExpandArtwork(false);
    }

    const addProductToCart = () => {
        const cartItem: ICartItem = {
            id: selectedVariant.id,
            price: selectedVariant.price ? selectedVariant.price : 0,
            productTitle: product.title,
            quantity: selectedQuantity,
            artwork: selectedArtwork,
            logoMockup: logoMockup,
            handle: product.handle,
            size: selectedVariant.title,
            decorationType: selectedDecorationType,
            decorationLocation: selectedDecorationLocation
        }
        // Don't allow user to add items to cart if the amount would exceed the amount available
        for (const itemInCart of cart.items) {
            if (itemInCart.productTitle === cartItem.productTitle && itemInCart.size === cartItem.size) {

                const variants = product.variants
                for (const variant of variants) {
                    if (variant.title === cartItem.size) {
                        if (cartItem.quantity + itemInCart.quantity > variant.quantityAvailable) {
                            return toast.error(`Unable to add. ${itemInCart.quantity} already in cart. Only ${variant.quantityAvailable} available.`)
                        }
                    }
                }
            }
        }
        dispatch(addToCart(cartItem));
        toast.success(`(${selectedQuantity}) ${product.title} added to cart!`);
    }

    const onQuantityChange = (newValue: number) => {
        if (newValue > 1) {
            if (newValue <= selectedVariant.quantityAvailable) {
                setSelectedQuantity(newValue);
            } else {
                toast.error(`Only ${selectedVariant.quantityAvailable} available in ${selectedVariant.title}`)
                setSelectedQuantity(selectedVariant.quantityAvailable)
            }
        } else {
            setSelectedQuantity(1);
        }
    }

    const scrollToProductDetails = () => {
        if (productInfoRef && productInfoRef.current) {
            const { y } = productInfoRef.current.getBoundingClientRect()
            scroll.scrollTo(y, {
                smooth: true
            })
        }
    }
    
    if (!product) {
        return <></>
    }

    return (
        <div>
            <Helmet>
                <meta
                    name="description"
                    content={description.length > 150 ? description.substring(0, 150) + "..." : description}
                />
                <title>{product.title}</title>
            </Helmet>
            <Loading isLoading={loading as boolean} /> 
            <div className="relative flex items-center flex-col w-full flex-0">
                <div className={`pdp-details-container ${expandArtwork ? "expanded" : ""} w-full`}>
                    <Card>
                        <div className={`pdp-details flex flex-col md:flex-row`}>
                            <div className={`pdp-artwork ${expandArtwork ? "expanded" : ""}`}>
                                {productImages && <Gallery images={productImages} hasThumbnails={true} expandFunction={expandFunction} closeExpandFunction={closeExpandFunction} />
                                }
                            </div>
                            <div className="pdp-options flex flex-col bg-merch-light-gray bg-opacity-40 w-full">
                                <h1 className="font-bold text-2xl">{product.title}</h1>
                                <p className="text-merch-text-disabled mt-5px mb-13">{selectedVariant.sku}</p>
                                <div className="flex">
                                    <p className="text-18 font-bold mb-26">${selectedVariant.price}</p>
                                    {selectedVariant.compareAtPriceV2 && selectedVariant.price && selectedVariant.compareAtPriceV2 > selectedVariant.price &&
                                        <p className="text-18 mb-26 line-through opacity-50 ml-8px">${selectedVariant.compareAtPriceV2.amount}</p>
                                    }
                                </div>
                                <div className="form-row">
                                    <label className="font-bold">Description:</label>
                                    <p className="text-length-150">{shortendedDescription}</p>
                                    <a href="javascript:void();" onClick={scrollToProductDetails} className="store-link block text-base text-merch-dark-gray font-medium underline mt-2">More Details</a>
                                </div>
                                {product.variants && product.variants.length > 0 &&
                                    <div className="mb-2">
                                        <FormSelect
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            name="sizes"
                                            label="Size"
                                            options={product.variants.map((option: IProductVariant) => {
                                                return { value: option.id, displayText: option.title ? option.title : "", disabled: option.quantityAvailable === 0 ? true : false }
                                            })}
                                            default={product.variants[0].id}
                                            onChange={(selectedOption: IFormOptionProps) => {
                                                if (product.variants) {
                                                    const selectedVariant = product.variants.find((v: IProductVariant) => v.id === selectedOption.value);
                                                    if (selectedVariant) {
                                                        setSelectedVariant(selectedVariant);
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                }
                                {artworkOptions.length > 0 &&
                                    <div className="mb-2">
                                        <FormSelect
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            name="artwork"
                                            label="Artwork"
                                            options={artworkOptions.map((option: string) => {
                                                if (option) {
                                                    const splitted = option.split("/")
                                                    const fileName = splitted[splitted.length - 1];
                                                    return { value: option, displayText: fileName }
                                                }
                                            })}
                                            artwork={true}
                                            default={artworkOptions[0]}
                                            onChange={(selectedOption: IFormOptionProps) => {
                                                setSelectedArtwork(selectedOption.value);
                                            }}
                                        />
                                    </div>
                                }
                                {decorationTypes.length > 0 &&
                                    <div className="mb-2">
                                        <FormSelect
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            name="decorationType"
                                            label="Decoration Type"
                                            options={decorationTypes.map((option: string) => { return { value: option, displayText: option } })}
                                            default={decorationTypes[0]}
                                            onChange={(selectedOption: IFormOptionProps) => {
                                                setSelectedDecorationType(selectedOption.value);
                                            }}
                                        />
                                    </div>
                                }
                                {decorationLocations.length > 0 &&
                                    <div className="mb-2">
                                        <FormSelect
                                            register={register}
                                            errors={errors}
                                            setValue={setValue}
                                            name="decorationLocation"
                                            label="Decoration Location"
                                            options={decorationLocations.map((option: string) => { return { value: option, displayText: option } })}
                                            default={decorationLocations[0]}
                                            onChange={(selectedOption: IFormOptionProps) => {
                                                setSelectedDecorationLocation(selectedOption.value);
                                            }}
                                        />
                                    </div>
                                }
                                <div className="flex w-full">
                                    <CTA disabled={isSoldOut} type="merchstore-primary" size="standard" classes="text-sm flex-grow-2" onClick={addProductToCart}>Add to Cart</CTA>
                                    <div className={`ml-2`}>
                                        <FormInputNumber
                                            name="quantity"
                                            required={true}
                                            register={register}
                                            errors={errors}
                                            defaultValue={selectedQuantity}
                                            onChange={onQuantityChange}
                                            positiveOnly={true}
                                            integerOnly={true}
                                            maxAllowed={selectedVariant.quantityAvailable} />
                                    </div>
                                </div>
                                {merchstore && merchstore.nextProcessingDate &&
                                    <div className="flex flex-col items-center mt-5">
                                        <p>Your order will be processed on:</p>
                                        <p className="text-lg font-bold">{getLocalDateText(merchstore.nextProcessingDate)}</p>
                                    </div>
                                }
                            </div>
                        </div>
                    </Card>
                </div>

            <div  ref={productInfoRef}></div>
            </div>

        </div>
    )
}